﻿/**
 * English UI strings. The reference dictionary every translation is keyed against.
 *
 * Approved copy from specification §9 appears here verbatim. `tests/content/copy.test.ts`
 * asserts the exact wording of the required lines, fails the build if any banned
 * absolute-privacy or financial-advice phrasing appears, and fails if an em dash appears
 * in any language.
 *
 * Adding a locale means adding a sibling file with the same keys and registering it in
 * `strings.ts`. No component changes.
 */

export const en = {
  common: {
    skipToContent: 'Skip to main content',
    loading: 'Loading…',
    tryAgain: 'Try again',
    close: 'Close',
    perPerson: 'per person',
    navHowItWorks: 'How it works',
    navPrivacy: 'Privacy',
    navFaq: 'FAQ',
    navGuides: 'Guides',
    navBlog: 'Articles',
    homeLink: 'Home',
    language: 'Language',
    chooseLanguage: 'Choose a language',
    amountInCurrency: (code: string) => `Amount in ${code}`,
    navLandmarkMain: 'Main',
    navLandmarkFooter: 'Footer',
  },

  landing: {
    heroHeadline: 'Agree on a budget without anyone sharing theirs.',
    heroSupport:
      'Create a private link. Everyone enters the most they’d feel comfortable spending. You only see the amount the whole group can live with.',
    previewTitle: 'What the group sees',
    previewNote: 'Rounded down from the lowest comfortable maximum.',
    previewExampleNote: 'Example only. No room has been created.',
  },

  createForm: {
    legend: 'Create a private group budget',
    titleLabel: 'What are you budgeting for?',
    titlePlaceholder: 'Weekend in Lisbon',
    titleHint: 'Everyone with the link sees this. Don’t put anything private in it.',
    peopleLabel: 'How many people?',
    peopleHint: 'Between 3 and 30, including you.',
    currencyLabel: 'Currency',
    amountLabel: 'Your comfortable maximum per person',
    amountHint: 'The most you’d be happy to spend. Not a promise, not a pledge.',
    submit: 'Create private budget',
    submitting: 'Creating your private link…',
    trustLine: 'Your number is never shown to the group.',
    errorTitleRequired: 'Add a short description so people know what this is for.',
    errorTitleTooLong: 'Keep this under 80 characters.',
    errorPeopleRange: 'Choose between 3 and 30 people. Three is the minimum for privacy.',
    errorAmountRequired: 'Enter the most you’d feel comfortable spending.',
    errorAmountInvalid: 'Enter an amount as a number, for example 300.',
    errorAmountTooPrecise: 'That’s more decimal places than this currency uses.',
    errorAmountTooLarge: 'That amount is too large.',
    errorGeneric:
      'Something went wrong creating the room. Your details are still here, so try again.',
    errorRateLimited: 'That’s a lot of rooms in a short time. Wait a minute and try again.',
    errorTurnstile: 'We couldn’t verify this browser. Reload the page and try once more.',
    amountEcho: (formatted: string) => `That’s ${formatted}`,
  },

  share: {
    title: 'Your private budget link is ready.',
    support:
      'Send it to everyone who should be included. The result appears when all expected answers are in.',
    linkLabel: 'Private room link',
    copy: 'Copy link',
    copied: 'Link copied',
    copyFailed: 'Couldn’t copy automatically. Select the link above and copy it.',
    share: 'Share link',
    shareTitle: 'Private group budget',
    shareText:
      'Enter the most you’d feel comfortable spending per person. Your amount stays private.',
    manageTitle: 'Keep your organiser link',
    manageBody:
      'This page’s address is also your organiser link. Bookmark it. It’s the only way to change the group size, reset or delete the room, and it can’t be recovered.',
    manageWarning: 'Don’t send the organiser link to the group. Send the link above instead.',
    continue: 'Go to the room',
  },

  room: {
    subtitle: 'Private group budget',
    question: 'What’s the most you’d feel comfortable spending per person?',
    questionSupport: 'Only the final group-safe cap will be shared.',
    amountLabel: 'Your comfortable maximum per person',
    submit: 'Submit privately',
    submitting: 'Saving…',
    update: 'Update my amount',
    edit: 'Edit my amount',
    cancelEdit: 'Cancel',
    savedTitle: 'Answer saved. You can edit it until the room completes.',
    yourAmount: 'Your amount',
    privacyTitle: 'Private by design',
    privacyPoints: [
      'No name or email',
      'Your amount is never shown',
      'Raw amounts are deleted after reveal',
    ],
    privacySentence: 'Your amount is never shown to the group.',
    progressLabel: (answered: number, expected: number) =>
      `${answered} of ${expected} people have answered`,
    progressAccessible: (answered: number, expected: number) =>
      `Progress: ${answered} of ${expected} expected answers received. No names are recorded.`,
    editHint: 'You can edit your answer until the room completes.',
    waitingTitle: 'Waiting for the rest of the group',
    waitingBody: 'The group-safe cap appears here as soon as everyone has answered.',
    refresh: 'Check for updates',
    revealing: 'Working out the group-safe cap…',
    fullTitle: 'This budget is already complete.',
    fullBody: 'No more answers can be added. Ask the organiser for the result.',
    errorSubmit: 'Your amount wasn’t saved. Nothing was sent to the group, so try again.',
    errorAmountInvalid: 'Enter an amount as a number, for example 300.',
    errorAmountRequired: 'Enter the most you’d feel comfortable spending.',
    errorRateLimited: 'Too many attempts. Wait a moment, then submit again.',
    errorConflict:
      'The room changed while you were typing. We’ve refreshed it, so check before submitting.',
  },

  result: {
    heading: 'The group can safely plan around',
    eyebrow: 'GROUP-SAFE CAP',
    meaningTitle: 'What this means',
    explanation:
      'This amount is rounded down and never exceeds the lowest comfortable maximum submitted.',
    privacyTitle: 'Privacy',
    deletion: 'Individual answers were never shown and have now been deleted.',
    repeatCta: 'Make one for your group',
    footnote: 'No names · No accounts · Temporary rooms',
    availableUntil: (date: string) =>
      `This result stays available until ${date}, then it’s deleted.`,
  },

  manage: {
    title: 'Organiser controls',
    subtitle: 'Only visible to you, from your organiser link.',
    peopleLabel: 'Expected number of people',
    increaseHelp: 'You can raise this at any time before the result appears.',
    decreaseBlocked:
      'You can’t lower this now that someone else has answered. Reset the room instead.',
    save: 'Update group size',
    saving: 'Updating…',
    saved: 'Group size updated.',
    resetTitle: 'Reset the room',
    resetBody: 'Deletes every answer, including yours, and starts the room again at zero.',
    reset: 'Reset room',
    resetConfirm: 'Reset this room? Every answer, including yours, is deleted immediately.',
    resetting: 'Resetting…',
    resetDone: 'Room reset. All answers were deleted.',
    deleteTitle: 'Delete the room',
    deleteBody: 'Removes the room and every answer straight away. The link stops working.',
    delete: 'Delete room',
    deleteConfirm:
      'Delete this room for good? Every answer is deleted immediately and the link stops working.',
    deleting: 'Deleting…',
    deletedTitle: 'Room deleted.',
    deletedBody: 'The room and every submitted amount have been deleted.',
    errorGeneric: 'That didn’t work. Reload your organiser link and try again.',
    errorAuth: 'This organiser link isn’t valid for this room.',
  },

  states: {
    notFoundTitle: 'This link doesn’t work.',
    notFoundBody:
      'The room may have been deleted by its organiser, or the link may be incomplete. Check the link you were sent.',
    expiredTitle: 'This private budget room expired and its submitted amounts were deleted.',
    expiredBody: 'Rooms last 7 days. Nothing from this one is kept.',
    createNew: 'Create a new budget',
    genericErrorTitle: 'Something went wrong.',
    genericErrorBody: 'The room couldn’t be loaded. Check your connection and try again.',
    offline: 'You appear to be offline.',
  },

  footer: {
    tagline: 'A private way for a group to agree one spending limit.',
    rights: 'No accounts. No names. Temporary rooms.',
    madeBy: (maker: string) => `Made by ${maker}`,
    groupTrips: 'Group trips',
    groupGifts: 'Group gifts',
  },

  ads: {
    label: 'Advertisement',
    inactive: (slot: string) => `Reserved ad space (${slot}), inactive`,
  },
};

/**
 * The contract every translation must satisfy.
 *
 * Deliberately inferred from `en` *without* `as const`. With `as const` each value would
 * be a literal type, so `strings.fr.ts` could never satisfy it: "Fermer" is not
 * assignable to "Close". Widened, the type still pins the exact shape, so a missing key
 * or a renamed one is a compile error in all three translations at once.
 */
export type Strings = typeof en;
